import { fetchDataWithRetry } from "@/lib/Fetchers/fetchData";
import { BookingInfo } from "@/lib/Types/DateListResponse/BookingInfo";
import { SpecialDealDetail } from "@/lib/Types/SpecialDealDetail";
import { sendKibanaLog } from "./sendKibanaLog";
import {
  VacancyBookingInfo,
  VacancyResponse,
} from "@/lib/Types/VacancyResponse/VacancyResponse";
import { ApiResponse } from "@/lib/Types/ApiError";
import { AppConfig } from "@/lib/Types/AppConfig";

// Parse URL rooms string (e.g., "25,25,4,4" or "25,25;25,25,4") into API format
function parseRoomsForApi(
  roomsString: string,
): Array<{ adults: number[]; children: number[] }> {
  return roomsString.split(";").map((room) => {
    const ages = room
      .split(",")
      .map(Number)
      .filter((age) => !isNaN(age));
    return {
      adults: ages.filter((age) => age >= 18),
      children: ages.filter((age) => age < 18),
    };
  });
}

interface VacancyPayload {
  bookingInfo: VacancyBookingInfo;
  bookOnOptionRuleId: string | null;
  channel: string;
  cid: number;
  departureAirport: string;
  departureDate: string;
  destinationAirport: string;
  m: number;
  p: number;
  returnDate: string;
  transactionId: string;
  hasFlexibleOffer: boolean;
  tourOperatorCode: string;
  rooms: Array<{ adults: number[]; children: number[] }>;
  device: string | undefined;
  debugMode?: boolean;
  toVoucherCodeRuleId?: string | null;
  ruleId?: string | null;
  upsellingOffer?: boolean;
}

interface FetchVacancyParams {
  // Offer data
  bookingInfo: BookingInfo;
  departureAirportCode: string;
  destinationAirportCode: string;
  departureDate: string; // offer.from
  returnDate: string; // offer.to
  tourOperatorCode: string;
  hasFlexibleOffer: boolean;
  ruleId: string | null;
  specialDealsDetails?: SpecialDealDetail[] | null;

  // From quickVacancy response (preferred) or searchParams
  transactionId: string;

  // From URL/context
  hotelCid: number;
  roomsString: string; // e.g., "25,25" or "25,25;25,25,4"
  skyscannerRedirectId: string | null;
  upsellingOffer?: boolean;

  // App config
  appConfig: AppConfig;
  searchId: string;
}

export async function fetchVacancy({
  bookingInfo,
  departureAirportCode,
  destinationAirportCode,
  departureDate,
  returnDate,
  tourOperatorCode,
  hasFlexibleOffer,
  ruleId,
  specialDealsDetails,
  transactionId,
  hotelCid,
  roomsString,
  skyscannerRedirectId,
  upsellingOffer,
  appConfig,
  searchId,
}: FetchVacancyParams): Promise<ApiResponse<VacancyResponse>> {
  const { envConfig, viewMode, userData } = appConfig;

  // Extract bookOnOptionRuleId from special deals
  const bookOnOptionDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "BOOK_ON_OPTION",
  );
  const bookOnOptionRuleId = bookOnOptionDeal?.ruleId?.toString() ?? null;

  // Extract toVoucherCodeRuleId from special deals
  const toVoucherDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "TO_VOUCHER",
  );
  const toVoucherCodeRuleId = toVoucherDeal?.ruleId?.toString() ?? null;

  // Pass bookingInfo as-is from offer/quickVacancy, only adding sessionId if missing
  // Don't default mustCheck - let the server determine it if not present in source
  const vacancyBookingInfo: VacancyBookingInfo = {
    ...bookingInfo,
    // Add sessionId from userData if not present in bookingInfo
    sessionId: bookingInfo.sessionId ?? userData?.sessionId,
  };

  const payload: VacancyPayload = {
    bookingInfo: vacancyBookingInfo,
    bookOnOptionRuleId,
    channel: viewMode.channel,
    cid: hotelCid,
    departureAirport: departureAirportCode,
    departureDate,
    destinationAirport: destinationAirportCode,
    m: viewMode.mandator,
    p: viewMode.profile,
    returnDate,
    transactionId,
    hasFlexibleOffer,
    tourOperatorCode,
    rooms: parseRoomsForApi(roomsString),
    toVoucherCodeRuleId,
    ...(ruleId && { ruleId }),
    device: userData?.deviceType,
    ...(skyscannerRedirectId && { skyscannerRedirectId }),
    ...(upsellingOffer && { upsellingOffer }),
  };

  const baseUrl = "comvel-vacancy2-service/rest/vacancy2";
  const url = `${envConfig.api}/${baseUrl}`;

  sendKibanaLog({
    envConfig,
    transactionId,
    message: "VACANCY START",
    component: "vacancyService",
    jsonMessage: payload,
    searchId: searchId,
    category: "debug",
  });

  const data = await fetchDataWithRetry({
    url,
    method: "POST",
    params: payload,
    envConfig,
    service: "vacancy",
    transactionId,
    searchId: searchId,
  });

  sendKibanaLog({
    envConfig,
    transactionId,
    message: `VACANCY ${data?.apiError ? "FAIL" : "SUCCESS"}`,
    component: "vacancyService",
    jsonMessage: data?.apiError ? data.message : { status: data?.status },
    searchId: searchId,
    category: "debug",
  });

  return data;
}
